"use client";

import dynamic from "next/dynamic";
import { useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";

import { PITRA_CHECK_FIELD_IDS, PITRA_CHECK_FORM, PITRA_CHECK_FRAME } from "./pitra-check-form";

// The /hi/pitra-dosha/ check's boundary (the rashi check's pattern,
// RashiCheckLazy): the server page imports only this module, and it owns the
// one dynamic() to the island. The form, place search, engine client and answer
// load as an async chunk; the first load gains only this file and the shell.
//
// Activation is element-scoped and local: no window listener and no timer, so
// a reader scrolling the article never downloads the island; a press, focus or
// submit on the shell does.
//
// The slot reserves the tallest state, so neither shell -> form nor a slow
// engine reply -> answer (or error) moves the page below. Natural heights
// measured 2026-10-11 (next dev + Playwright, 13 widths, the engine mocked with
// both answers of both checks, an engine failure and a typed place nobody
// picked): the shell is 420; the form 434 at 320 px wide, 414 at 360-414, 398
// at 480-639 and 356 from 640 px. The tallest state is the form with the
// engine-failure alert: 520 at 320 px, 500 at 360-390, 482 at 414, 466 at
// 480-639 and 424 from 640 px up. The answers are 160-326. Hence 560 px on
// phones and 460 px from the sm breakpoint, about a line of headroom each; with
// them the article below did not move in any state. translate="no" covers the
// shell, form and answer, which re-render on "check another"
// (tests/translate-safe-islands.test.ts).
const SHELL_PROPS = { ...PITRA_CHECK_FORM, className: PITRA_CHECK_FRAME } as const;

const PitraCheckHi = dynamic(() => import("./PitraCheckHi"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL_PROPS} />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: PITRA_CHECK_FIELD_IDS.date,
  tob: PITRA_CHECK_FIELD_IDS.time,
  place: PITRA_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || PITRA_CHECK_FIELD_IDS.date;
}

export default function PitraCheckHiLazy() {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // Focus follows the visitor into the real form after a keyboard focus, a
  // mouse press or a submit, and never after a touch, which may start a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(PITRA_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-pitra-check-slot=""
      className="flex min-h-[560px] flex-col sm:min-h-[460px]"
      {...shellIntent}
    >
      {active ? (
        <PitraCheckHi focusFieldId={focusFieldId} />
      ) : (
        <BirthToolFormShell {...SHELL_PROPS} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
